import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { Loader2 } from 'lucide-react';

const optionGroupTypes = [
  { value: 'ACCOMMODATION', label: 'Accommodation' },
  { value: 'TRANSFER', label: 'Transfer' },
  { value: 'MEAL', label: 'Meal' },
  { value: 'ACTIVITY', label: 'Activity' },
  { value: 'OTHER', label: 'Other' },
];

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  type: z.enum(['ACCOMMODATION', 'TRANSFER', 'MEAL', 'ACTIVITY', 'OTHER']),
  isRequired: z.boolean(),
  minSelections: z.number().min(0),
  maxSelections: z.number().min(1),
  sortOrder: z.number(),
});

type FormData = z.infer<typeof formSchema>;

interface OptionGroupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  packageId: string;
  group?: {
    id: string;
    name: string;
    description: string | null;
    type: string;
    isRequired: boolean;
    minSelections: number;
    maxSelections: number;
    sortOrder: number;
  };
  onSuccess?: () => void;
}

export function OptionGroupModal({
  open,
  onOpenChange,
  packageId,
  group,
  onSuccess,
}: OptionGroupModalProps) {
  const isEditing = !!group;

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      description: '',
      type: 'OTHER',
      isRequired: false,
      minSelections: 0,
      maxSelections: 1,
      sortOrder: 0,
    },
  });

  useEffect(() => {
    if (group) {
      form.reset({
        name: group.name,
        description: group.description || '',
        type: group.type as FormData['type'],
        isRequired: group.isRequired,
        minSelections: group.minSelections,
        maxSelections: group.maxSelections,
        sortOrder: group.sortOrder,
      });
    } else {
      form.reset({
        name: '',
        description: '',
        type: 'OTHER',
        isRequired: false,
        minSelections: 0,
        maxSelections: 1,
        sortOrder: 0,
      });
    }
  }, [group, form]);

  const createMutation = trpc.packageOption.createGroup.useMutation({
    onSuccess: () => {
      showSuccessToast('Option group created');
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error) => {
      showErrorToast(error.message || 'Failed to create option group');
    },
  });

  const updateMutation = trpc.packageOption.updateGroup.useMutation({
    onSuccess: () => {
      showSuccessToast('Option group updated');
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error) => {
      showErrorToast(error.message || 'Failed to update option group');
    },
  });

  const onSubmit = (data: FormData) => {
    if (isEditing && group) {
      updateMutation.mutate({
        id: group.id,
        ...data,
      });
    } else {
      createMutation.mutate({
        packageId,
        ...data,
      });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Option Group' : 'Add Option Group'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              placeholder="e.g., Hotel Upgrade Options"
              {...form.register('name')}
            />
            {form.formState.errors.name && (
              <p className="text-sm text-red-500">
                {form.formState.errors.name.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Optional description for this option group"
              rows={2}
              {...form.register('description')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="type">Type *</Label>
            <Select
              value={form.watch('type')}
              onValueChange={(value) => form.setValue('type', value as FormData['type'])}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {optionGroupTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="isRequired">Required</Label>
              <p className="text-sm text-muted-foreground">
                Customers must select an option from this group
              </p>
            </div>
            <Switch
              id="isRequired"
              checked={form.watch('isRequired')}
              onCheckedChange={(checked) => {
                form.setValue('isRequired', checked);
                if (checked && form.watch('minSelections') === 0) {
                  form.setValue('minSelections', 1);
                }
              }}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="minSelections">Min Selections</Label>
              <Input
                id="minSelections"
                type="number"
                min={0}
                {...form.register('minSelections', { valueAsNumber: true })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="maxSelections">Max Selections</Label>
              <Input
                id="maxSelections"
                type="number"
                min={1}
                {...form.register('maxSelections', { valueAsNumber: true })}
              />
              <p className="text-xs text-muted-foreground">
                1 = single choice, &gt;1 = multi-select
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sortOrder">Sort Order</Label>
            <Input
              id="sortOrder"
              type="number"
              {...form.register('sortOrder', { valueAsNumber: true })}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
